import { useEffect, useRef, useState } from "react";
import type { ExperienceSpec } from "@luxidar/experience-schema";
import type { BrowserExperience } from "@luxidar/experience-browser-integration";

interface PreviewProps {
  spec: ExperienceSpec | null;
  onActiveChange(active: boolean): void;
  onError(message: string): void;
}

export function Preview({ spec, onActiveChange, onError }: PreviewProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const shellRef = useRef<HTMLElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const recenterRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const integrationRef = useRef<BrowserExperience | undefined>(undefined);
  const [mode, setMode] = useState<"unavailable" | "simulator">("unavailable");
  const [orientation, setOrientation] = useState({ yawDeg: 0, pitchDeg: 0, rollDeg: 0 });

  useEffect(() => {
    let cancelled = false;
    if (!spec || !triggerRef.current || !shellRef.current || !mountRef.current || !closeRef.current || !recenterRef.current || !statusRef.current) return;
    shellRef.current.hidden = true;
    void import("@luxidar/experience-browser-integration").then(async ({ createBrowserExperience }) => {
      if (cancelled) return;
      const integration = createBrowserExperience({
        trigger: triggerRef.current!,
        shell: shellRef.current!,
        mount: mountRef.current!,
        exitButton: closeRef.current!,
        recenterButton: recenterRef.current!,
        status: statusRef.current!,
        spec,
        spatialMode: mode,
        manualSimulatorControls: mode === "simulator"
      });
      integration.attach();
      integrationRef.current = integration;
      await integration.open();
      if (cancelled) return;
      const snapshot = integration.snapshot();
      if (snapshot.lifecycleState === "error") onError(snapshot.lastError?.message ?? "Preview could not open.");
      else onActiveChange(true);
    }).catch((cause: unknown) => onError(cause instanceof Error ? cause.message : "Preview could not load."));
    return () => {
      cancelled = true;
      const integration = integrationRef.current;
      integrationRef.current = undefined;
      if (integration) void integration.dispose();
    };
  }, [spec, mode, onActiveChange, onError]);

  const restart = async () => {
    const integration = integrationRef.current;
    if (!integration) return;
    await integration.close();
    await integration.open();
    onActiveChange(integration.snapshot().lifecycleState === "active");
  };

  const updateOrientation = (key: keyof typeof orientation, value: number) => {
    const next = { ...orientation, [key]: value };
    setOrientation(next);
    integrationRef.current?.setSimulatorOrientation(next);
  };

  return (
    <section className="preview-panel" aria-labelledby="preview-heading">
      <div className="preview-heading-row">
        <div>
          <p className="eyebrow">03 / Preview</p>
          <h2 id="preview-heading">See it in space</h2>
        </div>
        {spec && <div className="segmented" aria-label="Preview mode">
          <button type="button" aria-pressed={mode === "unavailable"} onClick={() => setMode("unavailable")}>Browser preview</button>
          <button type="button" aria-pressed={mode === "simulator"} onClick={() => setMode("simulator")}>Simulate 3DoF</button>
        </div>}
      </div>

      {!spec && <div className="preview-empty" data-testid="preview-empty">
        <div className="preview-orb" aria-hidden="true"><span /></div>
        <p>Your spatial preview will appear here.</p>
        <small>Add a model and describe the experience to begin.</small>
      </div>}

      <button ref={triggerRef} type="button" hidden>Open preview</button>
      <section ref={shellRef} className="inline-viewer" data-testid="preview-shell" aria-label="Experience preview">
        <div className="viewer-toolbar">
          <span>{mode === "simulator" ? "3DoF simulation" : "Interactive Web3D"}</span>
          <div>
            <button type="button" onClick={() => void restart()}>Restart</button>
            <button ref={recenterRef} type="button">Recenter</button>
            <button ref={closeRef} type="button" onClick={() => onActiveChange(false)}>Close</button>
          </div>
        </div>
        <div ref={mountRef} className="viewer-mount" data-testid="viewer-mount" />
        <div ref={statusRef} className="viewer-status" role="status" aria-live="polite" />
      </section>

      {spec && mode === "simulator" && <div className="simulator-controls" aria-label="Simulated head movement">
        {(["yawDeg", "pitchDeg", "rollDeg"] as const).map((key) => <label key={key}>
          <span>{key.replace("Deg", "")}</span>
          <input type="range" min="-45" max="45" value={orientation[key]} onChange={(event) => updateOrientation(key, Number(event.target.value))} />
          <output>{orientation[key]}°</output>
        </label>)}
      </div>}
    </section>
  );
}
